<template>
  <a-modal
    :title="$t('all.edit') + ' ' + $t('profil.paiementScol')"
    :visible="visible"
    :width="800"
    :confirm-loading="loading"
    @ok="handleOk"
    @cancel="handleCancel"
    :ok-text="$t('action.enregistrer')"
    :cancel-text="$t('action.annuler')"
  >
    <div v-if="scolariteData">
      <div class="mb-3">
        <h5>{{ scolariteData.name }}</h5>
        <a-tag :color="scolariteData.mode === 'M' ? 'blue' : 'green'">
          {{ scolariteData.mode === 'M' ? $t('inscription.monthlyPayment') : $t('inscription.installmentPayment') }}
        </a-tag>
      </div>

      <!-- Month Selection for Mode M -->
      <div v-if="scolariteData.mode === 'M'" class="month-selection-container">
        <h6>{{ $t('inscription.selectionnerMois') }}:</h6>
        <div class="month-grid">
          <div 
            v-for="month in availableMonths" 
            :key="month.value"
            class="month-item"
            :class="{ 
              'selected': isMonthSelected(month.value),
              'paid': getMonthPaymentStatus(month.value) === 'paid',
              'partially-paid': getMonthPaymentStatus(month.value) === 'partially-paid'
            }"
            @click="toggleMonthSelection(month.value)"
          >
            <div class="month-label">{{ month.label }}</div>
            <div class="month-status">
              <a-tag 
                :color="getMonthTagColor(month.value)" 
                size="small"
              >
                {{ getMonthStatusLabel(month.value) }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Tranche Selection for Mode T -->
      <div v-else-if="scolariteData.mode === 'T'" class="tranche-selection-container">
        <h6>{{ $t('inscription.selectionnerTranches') }}:</h6>
        <div class="tranche-grid">
          <div 
            v-for="tranche in availableTranches" 
            :key="tranche.trancheNumber"
            class="tranche-item"
            :class="{ 
              'selected': isTrancheSelected(tranche.trancheNumber),
              'paid': getTranchePaymentStatus(tranche.trancheNumber) === 'paid',
              'partially-paid': getTranchePaymentStatus(tranche.trancheNumber) === 'partially-paid'
            }"
            @click="toggleTrancheSelection(tranche.trancheNumber)"
          >
            <div class="tranche-label">{{ $t('inscription.tranche') }} {{ tranche.trancheNumber }}</div>
            <div class="tranche-status">
              <a-tag 
                :color="getTrancheTagColor(tranche.trancheNumber)" 
                size="small"
              >
                {{ getTrancheStatusLabel(tranche.trancheNumber) }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Months/Tranches Summary -->
      <div class="mt-3">
        <h6>{{ getSummaryTitle() }}:</h6>
        <div class="selected-days">
          <a-tag
            v-for="day in selectedDays"
            :key="day.date"
            :color="getTagColor(day)"
            :closable="!day.paid && !day.remaining"
            @close="removeSelectedDate(day.date)"
            class="mb-2 mr-2"
          >
            {{ formatFeeName(day.date, false) }} - {{ getTagLabel(day) }}
          </a-tag>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import moment from 'moment';
import { formatFeeName } from "@/utilities/settings";
import apiClient from "@/services/axios";
import { mapState } from "vuex";

export default {
  name: 'EditScolariteModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    scolariteData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      selectedDays: [], // Array of day objects with payment info
      originalDays: [], // Store original days for comparison
    };
  },
  computed: {
    ...mapState(["settings"]),
    availableMonths() {
      if (!this.scolariteData || this.scolariteData.mode !== 'M') return [];
      
      const months = [];
      
      // Get school year dates from settings
      const schoolarYears = this.settings?.schoolarYears || [];
      const activeSchoolarYear = this.settings?.activeSchoolarYear;
      
      let start, end;
      
      if (schoolarYears.length > 0 && activeSchoolarYear) {
        const schoolarYear = schoolarYears.find(sc => sc._id === activeSchoolarYear);
        if (schoolarYear) {
          start = moment(schoolarYear.start);
          end = moment(schoolarYear.end);
        }
      }
      
      // Fallback to current year if no school year found
      if (!start || !end) {
        start = moment().startOf('year');
        end = moment().endOf('year');
      }
      
      let current = start.clone().startOf('month');
      while (current.isSameOrBefore(end, 'month')) {
        months.push({
          value: current.format('YYYY-MM-01'),
          label: current.format('MMMM YYYY')
        });
        current.add(1, 'month');
      }
      
      return months;
    },
    availableTranches() {
      if (!this.scolariteData || this.scolariteData.mode !== 'T') return [];
      
      // Get tranches from scolarite data
      return this.scolariteData.tranches || [];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.scolariteData) {
        this.initializeData();
      }
    },
    scolariteData: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initializeData();
        }
      },
      deep: true
    }
  },
  methods: {
    formatFeeName,
    initializeData() {
      if (!this.scolariteData || !this.scolariteData.fees) return;      
      this.selectedDays = this.scolariteData.fees
        .filter(fee => fee.feeName !== 'inscription')
        .map(fee => ({
          date: fee.feeName,
          paid: fee.isPayed.fullyPaid,
          remaining: fee.isPayed.remaining && fee.isPayed.remaining>0,
          feeName: fee.feeLabel,
        }));

      this.originalDays = [...this.selectedDays];
    },

    removeSelectedDate(dateStr) {
      const dayIndex = this.selectedDays.findIndex(d => d.date === dateStr);
      if (dayIndex > -1) {
        this.selectedDays.splice(dayIndex, 1);
      }
    },

    getTagColor(day) {
      if (day.paid) {
        return 'green';
      } else if (day.remaining) {
        return 'orange';
      } else {
        return 'red';
      }
    },

    getTagLabel(day) {
      if (day.paid) {
        return this.$t('paiement.paye');
      } else if (day.remaining) {
        return this.$t('profil.paidWithAvance');
      } else {
        return this.$t('paiement.impaye');
      }
    },

    // Month selection methods for mode M
    isMonthSelected(monthValue) {
      return this.selectedDays.some(day => day.date === monthValue);
    },

    getMonthPaymentStatus(monthValue) {
      const day = this.selectedDays.find(d => d.date === monthValue);
      if (!day) return 'unpaid';
      
      if (day.paid) return 'paid';
      if (day.remaining) return 'partially-paid';
      return 'unpaid';
    },

    getMonthTagColor(monthValue) {
      const status = this.getMonthPaymentStatus(monthValue);
      if (status === 'paid') return 'green';
      if (status === 'partially-paid') return 'orange';
      return 'red';
    },

    getMonthStatusLabel(monthValue) {
      const status = this.getMonthPaymentStatus(monthValue);
      if (status === 'paid') return this.$t('paiement.paye');
      if (status === 'partially-paid') return this.$t('profil.paidWithAvance');
      return this.$t('paiement.impaye');
    },

    toggleMonthSelection(monthValue) {
      const existingDay = this.selectedDays.find(day => day.date === monthValue);
      
      if (existingDay) {
        // Check if it's paid or partially paid - don't allow removal
        if (existingDay.paid || existingDay.remaining) {
          return; // Can't remove paid/partially paid months
        }
        // Remove unpaid month
        this.removeSelectedDate(monthValue);
      } else {
        // Add new month
        this.addSelectedMonth(monthValue);
      }
    },

    addSelectedMonth(monthValue) {
      const newDay = {
        date: monthValue,
        paid: false,
        remaining: false,
        feeName: moment(monthValue).format('MMMM YYYY'),
      };
      
      this.selectedDays.push(newDay);
    },

    // Tranche selection methods for mode T
    isTrancheSelected(trancheNumber) {
      return this.selectedDays.some(day => day.date === trancheNumber.toString());
    },

    getTranchePaymentStatus(trancheNumber) {
      const day = this.selectedDays.find(d => d.date === trancheNumber.toString());
      if (!day) return 'unpaid';
      
      if (day.paid) return 'paid';
      if (day.remaining) return 'partially-paid';
      return 'unpaid';
    },

    getTrancheTagColor(trancheNumber) {
      const status = this.getTranchePaymentStatus(trancheNumber);
      if (status === 'paid') return 'green';
      if (status === 'partially-paid') return 'orange';
      return 'red';
    },

    getTrancheStatusLabel(trancheNumber) {
      const status = this.getTranchePaymentStatus(trancheNumber);
      if (status === 'paid') return this.$t('paiement.paye');
      if (status === 'partially-paid') return this.$t('profil.paidWithAvance');
      return this.$t('paiement.impaye');
    },

    toggleTrancheSelection(trancheNumber) {
      const existingDay = this.selectedDays.find(day => day.date === trancheNumber.toString());
      
      if (existingDay) {
        // Check if it's paid or partially paid - don't allow removal
        if (existingDay.paid || existingDay.remaining) {
          return; // Can't remove paid/partially paid tranches
        }
        // Remove unpaid tranche
        this.removeSelectedDate(trancheNumber.toString());
      } else {
        // Add new tranche
        this.addSelectedTranche(trancheNumber);
      }
    },

    addSelectedTranche(trancheNumber) {
      const tranche = this.availableTranches.find(t => t.trancheNumber === trancheNumber);
      if (!tranche) return;

      const newDay = {
        date: trancheNumber.toString(),
        paid: false,
        remaining: false,
        feeName: `${this.$t('inscription.tranche')} ${trancheNumber}`,
      };
      
      this.selectedDays.push(newDay);
    },

    getSummaryTitle() {
      if (this.scolariteData.mode === 'M') {
        return this.$t('inscription.moisSelectionnes');
      } else if (this.scolariteData.mode === 'T') {
        return this.$t('inscription.tranchesSelectionnees');
      }
      return this.$t('inscription.elementsSelectionnes');
    },

    async handleOk() {
      this.loading = true;
      
      try {
        const unpaidFees = this.selectedDays
          .filter(day => !day.paid && !day.remaining)
          .map(day => day.date);

        await apiClient.patch(`/payment/fees/${this.scolariteData._id}`, {
          fees: unpaidFees
        });
        
        this.$message.success(this.$t('success.modification'));
        await this.$emit('updatePayment');
        this.handleCancel();
      } catch (error) {
        this.$message.error(this.$t('error.modif'));
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('close');
      this.resetData();
    },

    resetData() {
      this.selectedDays = [];
      this.originalDays = [];
    }
  }
};
</script>

<style scoped>
.selected-days {
  max-height: 120px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  background: #f9f9f9;
}

/* Month selection styles */
.month-selection-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
  margin-bottom: 16px;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.month-item {
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  text-align: center;
}

.month-item:hover:not(.paid):not(.partially-paid) {
  border-color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.month-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.month-item.paid {
  border-color: #52c41a;
  background-color: #f6ffed;
  cursor: not-allowed;
  opacity: 0.8;
}

.month-item.partially-paid {
  border-color: #faad14;
  background-color: #fffbe6;
  cursor: not-allowed;
  opacity: 0.8;
}

.month-label {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 8px;
}

.month-status {
  display: flex;
  justify-content: center;
}

/* Tranche selection styles */
.tranche-selection-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
  margin-bottom: 16px;
}

.tranche-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.tranche-item {
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  text-align: center;
}

.tranche-item:hover:not(.paid):not(.partially-paid) {
  border-color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.tranche-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.tranche-item.paid {
  border-color: #52c41a;
  background-color: #f6ffed;
  cursor: not-allowed;
  opacity: 0.8;
}

.tranche-item.partially-paid {
  border-color: #faad14;
  background-color: #fffbe6;
  cursor: not-allowed;
  opacity: 0.8;
}

.tranche-label {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 8px;
}

.tranche-status {
  display: flex;
  justify-content: center;
}
</style>
